/**
 * Recovery & Sleep detail — pure presentation helpers.
 *
 * Deliberately NOT an *Engine.jsx file: the sealed engines are locked. Nothing
 * here touches state, the network or the DOM — it only turns the derived
 * payload from `GET /longevity/me/sleep` into labels, colours and widths.
 */

export const RANGES = [
  { key: "D", label: "D" },
  { key: "W", label: "W" },
  { key: "M", label: "M" },
  { key: "3M", label: "3M" },
  { key: "Y", label: "Y" },
];

export const STAGE_COLOR = {
  awake: "#e2808f",
  restless: "#73b06f",
  rem: "#8fd0e8",
  light: "#6f9fd8",
  deep: "#7a6bb5",
};

export const SERIES_COLOR = {
  total: "#5fb8c9",
  main: "#9d8fd8",
  poor: "#e2808f",
  fair: "#d8a24a",
  good: "#5fb8c9",
  excellent: "#4b8f68",
  band: "#4b8f68",
};

export const bandDotColor = (label) => ({
  "Needs attention": SERIES_COLOR.poor,
  Fair: SERIES_COLOR.fair,
  Good: SERIES_COLOR.good,
  Excellent: SERIES_COLOR.excellent,
}[label] || "#999");

export const NOT_TRACKED = "Not tracked by your device yet";

export const BAND = {
  Excellent: { bg: "#e8f3ec", fg: "#2f6b45", ring: "#2f6b45" },
  Good: { bg: "#eef3e8", fg: "#4b6b2f", ring: "#4b6b2f" },
  Fair: { bg: "#fdf3e3", fg: "#8a6321", ring: "#8a6321" },
  "Needs attention": { bg: "#fbecec", fg: "#8c3a3a", ring: "#8c3a3a" },
};

export const band = (label) => BAND[label] || { bg: "#f2f2ef", fg: "#555", ring: "#999" };

/** "2026-10-25" → "Today" / "Yesterday" / "Sat, 25 Oct 2026" */
export function dayHeading(date) {
  if (!date) return "—";
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const yesterday = new Date(today.getTime() - 86400000);
  if (date === iso(today)) return "Today";
  if (date === iso(yesterday)) return "Yesterday";
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
}

export const shortDay = (date) => {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const hoursFromMinutes = (m) => (m === null || m === undefined ? null : Number((m / 60).toFixed(2)));

export function rangeCaption(range, days) {
  if (range === "W") return "Last 7 nights";
  if (range === "M") return "Last 30 nights";
  if (range === "3M") return "Last 3 months";
  if (range === "Y") return "Last 12 months";
  return `Last ${days} nights`;
}

/**
 * A stage with no per-minute segments still needs a bar — fall back to one
 * proportional block so an older sync never renders an empty row.
 */
export function stageBars(stage) {
  if (stage.segments?.length) return stage.segments;
  if (!stage.share_pct) return [];
  return [{ start_pct: 0, width_pct: Math.max(0.6, stage.share_pct) }];
}
